import { useEffect, useState } from 'react';

interface LoginSuccessAnimationProps {
  userName?: string;
  onComplete?: () => void;
}

export const LoginSuccessAnimation = ({ userName, onComplete }: LoginSuccessAnimationProps) => {
  const [visible, setVisible] = useState(true);
  const [phase, setPhase] = useState<'check' | 'text' | 'done'>('check');

  useEffect(() => {
    const textTimer = setTimeout(() => setPhase('text'), 400);
    const doneTimer = setTimeout(() => setPhase('done'), 2200);
    const dismissTimer = setTimeout(() => {
      setVisible(false);
      onComplete?.();
    }, 2600);

    return () => {
      clearTimeout(textTimer);
      clearTimeout(doneTimer);
      clearTimeout(dismissTimer);
    };
  }, [onComplete]);

  if (!visible) return null;

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-[60] flex items-center justify-center pointer-events-none">
      {/* Floating particles */}
      <div className="absolute inset-0 overflow-hidden">
        {Array.from({ length: 20 }).map((_, i) => (
          <div
            key={i}
            className="absolute w-2 h-2 rounded-full animate-confetti"
            style={{
              left: `${Math.random() * 100}%`,
              top: '-10px',
              backgroundColor: ['#f97316', '#fb923c', '#fdba74', '#fed7aa', '#fff7ed'][i % 5],
              animationDelay: `${Math.random() * 0.8}s`,
              animationDuration: `${1.5 + Math.random() * 1.5}s`,
              width: `${4 + Math.random() * 8}px`,
              height: `${4 + Math.random() * 8}px`,
            }}
          />
        ))}
      </div>

      <div className="text-center relative z-10">
        {/* Checkmark circle */}
        <div className="relative mb-6 mx-auto w-28 h-28">
          {/* Pulse ring */}
          <div className="absolute inset-0 rounded-full bg-orange-500/20 animate-ping" style={{ animationDuration: '1.5s' }} />

          {/* Main circle */}
          <div className="w-28 h-28 rounded-full bg-gradient-to-br from-orange-500 to-orange-600 flex items-center justify-center relative shadow-2xl shadow-orange-500/30">
            {/* Checkmark SVG */}
            <svg
              className="w-16 h-16 text-white"
              viewBox="0 0 52 52"
              fill="none"
            >
              <circle
                cx="26"
                cy="26"
                r="25"
                stroke="white"
                strokeWidth="2"
                strokeDasharray="157"
                strokeDashoffset={phase === 'check' ? '157' : '0'}
                className="transition-all duration-500 ease-out"
              />
              <path
                d="M14 27 L22 35 L38 19"
                stroke="white"
                strokeWidth="3"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeDasharray="40"
                strokeDashoffset={phase === 'check' ? '40' : '0'}
                className="transition-all duration-500 ease-out delay-200"
              />
            </svg>
          </div>
        </div>

        {/* Success text */}
        <div
          className={`transition-all duration-500 ${
            phase === 'text' || phase === 'done'
              ? 'opacity-100 translate-y-0'
              : 'opacity-0 translate-y-4'
          }`}
        >
          <h2 className="text-4xl font-bold text-white mb-2">
            Login Realizado!
          </h2>
          <p className="text-xl text-orange-200 font-medium">
            {userName ? `Bem-vindo de volta, ${userName}!` : 'Bem-vindo de volta!'}
          </p>
        </div>

        {/* Subtle glow ring */}
        <div className="absolute inset-0 -z-10">
          <div className="w-64 h-64 mx-auto rounded-full bg-orange-500/10 blur-3xl animate-pulse" />
        </div>
      </div>
    </div>
  );
};